import { useEffect, useState } from "react";
import { Loader2, Users, Rocket, Cpu, DollarSign, Laptop, AlertTriangle } from "lucide-react";
import AppNav from "@/components/AppNav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [limits, setLimits] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingBridge, setSavingBridge] = useState(false);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/admin/role-limits").then(({ data }) => setLimits(data));
    api.get("/admin/settings").then(({ data }) => setSettings(data));
  };
  useEffect(load, []);

  const setRole = async (userId, role) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleBridge = async (enabled) => {
    setSavingBridge(true);
    try {
      await api.put("/admin/settings", { local_bridge_enabled: enabled });
      setSettings((s) => ({ ...s, local_bridge: { ...s.local_bridge, enabled } }));
      toast.success(enabled ? "Local bridge enabled" : "Local bridge disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingBridge(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-app text-2xl font-semibold mb-6">Admin control</h1>

        <div className="grid sm:grid-cols-4 gap-px bg-border border border-border rounded-xl overflow-hidden mb-10">
          <Stat icon={Users} label="Users" value={stats?.users ?? "—"} />
          <Stat icon={Rocket} label="Projects" value={stats?.projects ?? "—"} />
          <Stat icon={Cpu} label="Generations" value={stats?.generations ?? "—"} />
          <Stat icon={DollarSign} label="Total cost" value={stats ? `$${stats.total_cost.toFixed(4)}` : "—"} mono />
        </div>

        {/* Local bridge */}
        <h2 className="font-app text-lg font-semibold mb-3">Local model bridge</h2>
        <div className="border border-border rounded-xl p-5 mb-10 bg-card">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <Laptop className="w-4 h-4 text-kinetic" />
                <span className="font-app text-sm font-semibold">Use your own Claude subscription</span>
              </div>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Routes your generations through a bridge on your own machine instead of a metered API
                key. Only your admin account can use it — everyone else keeps using their own keys.
              </p>
              <p className="text-xs text-zinc-500 mt-2 max-w-2xl">
                Your subscription covers you, not other people. Serving other accounts through it would
                breach Anthropic&apos;s terms, so the server refuses it for any non-admin account
                regardless of this switch.
              </p>

              {settings && !settings.local_bridge.configured && (
                <div data-testid="bridge-not-configured"
                  className="mt-3 flex items-start gap-2 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-md p-2.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Set <code className="font-mono">LOCAL_BRIDGE_URL</code> on the Worker before turning
                    this on — point it at the Cloudflare Tunnel in front of your machine.
                  </span>
                </div>
              )}

              {settings?.local_bridge.enabled && settings.local_bridge.models.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {settings.local_bridge.models.map((m) => (
                    <Badge key={m.id} variant="outline" className="border-border font-mono text-[10px] text-zinc-400">
                      {m.label}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="shrink-0 pt-1">
              {settings === null ? (
                <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
              ) : (
                <Switch
                  data-testid="local-bridge-toggle"
                  checked={settings.local_bridge.enabled}
                  disabled={savingBridge || !settings.local_bridge.configured}
                  onCheckedChange={toggleBridge}
                  aria-label="Enable the local model bridge"
                />
              )}
            </div>
          </div>
        </div>

        {/* Role limits */}
        <h2 className="font-app text-lg font-semibold mb-3">Role limits</h2>
        <div className="border border-border rounded-xl overflow-hidden mb-10">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Role</TableHead><TableHead>Projects</TableHead>
                <TableHead>Gen/day</TableHead><TableHead>Gen/month</TableHead>
                <TableHead>Max files</TableHead><TableHead>Models</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {limits.map((l) => (
                <TableRow key={l.role} className="border-border font-mono text-sm">
                  <TableCell className="uppercase text-kinetic">{l.role}</TableCell>
                  <TableCell>{l.max_projects}</TableCell>
                  <TableCell>{l.gen_per_day}</TableCell>
                  <TableCell>{l.gen_per_month}</TableCell>
                  <TableCell>{l.max_app_files}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{l.allowed_models.length} models</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Users */}
        <h2 className="font-app text-lg font-semibold mb-3">Users</h2>
        <div className="border border-border rounded-xl overflow-hidden">
          {users === null ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>User</TableHead><TableHead>Role</TableHead>
                  <TableHead>Projects</TableHead><TableHead>Gens</TableHead><TableHead>Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-border">
                    <TableCell>
                      <div className="font-app">{u.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
                    </TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                        <SelectTrigger data-testid={`role-select-${u.id}`} className="w-28 h-8 bg-background border-border font-mono text-xs uppercase">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          {["standard", "pro", "gold", "admin"].map((r) => (
                            <SelectItem key={r} value={r} className="font-mono text-xs uppercase">{r}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="font-mono">{u.projects}</TableCell>
                    <TableCell className="font-mono">{u.generations}</TableCell>
                    <TableCell className="font-mono text-kinetic">${u.total_cost.toFixed(4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, mono }) {
  return (
    <div className="bg-card p-6">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" /><span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-3xl font-semibold ${mono ? "font-mono text-kinetic" : "font-app"}`}>{value}</p>
    </div>
  );
}
